import CONFIG from "../config/config";
import { apiFetch } from "./http";

/**
 * notifications.api.js — client for the customer-portal notifications inbox.
 *
 * Backend contract (controller/portalNotifications.controller.js), gated by
 * verifyPortalToken:
 *   GET  /api/portal/notifications?page=&limit=&unreadOnly=&type=&severity=&roverType=&serialID=&q=&from=&to=
 *        -> { notifications, total, unreadCount, page }
 *   GET  /api/portal/notifications/stats?<same content filters>  -> { total, unread, ...severityCounts }
 *   GET  /api/portal/notifications/unread-count                  -> { count }
 *   PUT  /api/portal/notifications/:id/read                      -> the updated notification
 *   PUT  /api/portal/notifications/mark-all-read                 -> { message }
 *
 * These are the PORTAL endpoints, not the admin ones (/api/notifications/*, which
 * the portal token cannot open and which deliberately expose every tenant's rows).
 * Every query here is scoped server-side to req.portalUser.portalUserId, taken from
 * the verified JWT rather than from anything the client sends — there is no userId
 * parameter to pass or to spoof. That makes the whole surface safe by construction:
 * the caller cannot widen it, so no client-side ownership filtering is needed for
 * these rows. The type/severity/q/from/to filters only ever narrow further within
 * that scope — they share the admin feed's filter-building logic server-side.
 *
 * Only the four read/mark endpoints exist: the portal never creates a notification,
 * so there is no create call here.
 */

const BASE_URL = `${CONFIG.BASE_URL}/api/portal/notifications`;

// Same auth convention as every other portal call in this app: the bearer token is
// read from localStorage at call time (there is no shared fetch wrapper or axios
// interceptor to hang it off).
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("portalToken")}`,
});

// Shared by listNotifications and getNotificationStats — every content filter
// is optional and omitted entirely when falsy, so the request line stays as
// short as whatever the caller actually set.
const buildParams = ({ type, severity, roverType, serialID, q, from, to, unreadOnly } = {}) => {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (severity) params.set("severity", severity);
  // Resolved server-side against the caller's OWN robots, so neither can widen
  // the feed — an unowned serial or unheld type simply matches nothing.
  if (roverType) params.set("roverType", roverType);
  if (serialID) params.set("serialID", serialID);
  if (q) params.set("q", q);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (unreadOnly) params.set("unreadOnly", "true");
  return params;
};

/**
 * One page of the caller's inbox, newest first.
 *
 * @param {{ page?: number, limit?: number, unreadOnly?: boolean, type?: string,
 *   severity?: string, roverType?: string, serialID?: string, q?: string,
 *   from?: string, to?: string }} [options]
 * @returns {Promise<{ notifications: Array<object>, total: number, unreadCount: number, page: number }>}
 */
export const listNotifications = async ({ page = 1, limit = 30, ...filters } = {}) => {
  const params = buildParams(filters);
  params.set("page", String(page));
  params.set("limit", String(limit));

  const res = await apiFetch(`${BASE_URL}?${params}`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to fetch notifications");

  const data = await res.json();
  return {
    notifications: Array.isArray(data.notifications) ? data.notifications : [],
    total: data.total ?? 0,
    unreadCount: data.unreadCount ?? 0,
    page: data.page ?? page,
  };
};

/**
 * Header-tile counts for the caller's own feed — total/unread plus a
 * per-severity breakdown, scoped by the same content filters as the list.
 *
 * @param {{ unreadOnly?: boolean, type?: string, severity?: string,
 *   roverType?: string, serialID?: string, q?: string, from?: string,
 *   to?: string }} [filters]
 * @returns {Promise<{ total: number, unread: number, info: number, success: number,
 *   warning: number, critical: number }>}
 */
export const getNotificationStats = async (filters = {}) => {
  const params = buildParams(filters);
  const res = await apiFetch(`${BASE_URL}/stats?${params}`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to fetch notification stats");
  return res.json();
};

/** The durable, all-time unread count for the badge — not a session-scoped tally. */
export const getUnreadCount = async () => {
  const res = await apiFetch(`${BASE_URL}/unread-count`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to fetch unread count");
  const data = await res.json();
  return data.count ?? 0;
};

/**
 * Mark one notification read. The backend matches on { _id, userId } together, so
 * another user's id resolves to 404 rather than to a cross-tenant write.
 */
export const markNotificationRead = async (id) => {
  const res = await apiFetch(`${BASE_URL}/${id}/read`, { method: "PUT", headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to mark notification read");
  return res.json();
};

/** Mark every unread notification of the caller read in one request. */
export const markAllNotificationsRead = async () => {
  const res = await apiFetch(`${BASE_URL}/mark-all-read`, {
    method: "PUT",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to mark all notifications read");
  return res.json();
};
